import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { FolderKanban, Brain, GitBranch, Activity, Award, HardDrive } from 'lucide-react';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const ACTIVITY_CONFIG = {
  model_trained:    { icon: Brain,        label: 'Model trained',    color: 'bg-purple-100 text-purple-600' },
  pipeline_updated: { icon: GitBranch,    label: 'Pipeline updated', color: 'bg-blue-100 text-blue-600' },
};

const Home = () => {
  const { user } = useAuth();
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/stats/me')
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n, decimals = 0) =>
    n == null ? '—' : Number(n).toFixed(decimals);

  const fmtPct = (v) =>
    v == null ? '—' : `${(v * 100).toFixed(1)}%`;

  const fmtStorage = (mb) => {
    if (mb == null) return '—';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(2)} MB`;
  };

  const StatCard = ({ icon: Icon, title, value, description, iconColor, iconBg }) => (
    <div className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-gray-500 text-xs font-medium mb-0.5">{title}</div>
          <div className="text-xl font-bold text-gray-900 mb-0.5">
            {loading ? <span className="inline-block h-6 w-16 bg-gray-100 rounded animate-pulse" /> : value}
          </div>
          <div className="text-gray-400 text-xs">{description}</div>
        </div>
        <div className={`p-2 rounded-lg ${iconBg}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );

  const recentActivity = stats?.recentActivity || [];

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {user?.name || 'User'}!
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Here's an overview of your ML workspace.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard
            icon={FolderKanban}
            title="Total Projects"
            value={fmt(stats?.totalProjects)}
            description="Machine learning projects"
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
          />
          <StatCard
            icon={GitBranch}
            title="Total Pipelines"
            value={fmt(stats?.totalPipelines)}
            description="Across all projects"
            iconBg="bg-indigo-100"
            iconColor="text-indigo-600"
          />
          <StatCard
            icon={Brain}
            title="Trained Models"
            value={fmt(stats?.totalModels)}
            description="Saved to your library"
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
          />
          <StatCard
            icon={Activity}
            title="Avg Accuracy / R²"
            value={fmtPct(stats?.avgAccuracy)}
            description="Across all completed models"
            iconBg="bg-green-100"
            iconColor="text-green-600"
          />
          <StatCard
            icon={Award}
            title="Best Model"
            value={fmtPct(stats?.bestAccuracy)}
            description="Highest accuracy achieved"
            iconBg="bg-amber-100"
            iconColor="text-amber-600"
          />
          <StatCard
            icon={HardDrive}
            title="Total Storage"
            value={fmtStorage(stats?.totalStorageMb)}
            description="Model files stored"
            iconBg="bg-gray-100"
            iconColor="text-gray-600"
          />
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-base font-semibold text-gray-900 mb-3">Recent Activity</h3>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center space-x-3 p-2 rounded-lg animate-pulse">
                  <div className="w-8 h-8 rounded-lg bg-gray-100" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 bg-gray-100 rounded w-1/3" />
                    <div className="h-2.5 bg-gray-50 rounded w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentActivity.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              No activity yet — create a project to get started.
            </p>
          ) : (
            <div className="space-y-1">
              {recentActivity.map((item, i) => {
                const cfg = ACTIVITY_CONFIG[item.type] || ACTIVITY_CONFIG.pipeline_updated;
                const Icon = cfg.icon;
                return (
                  <div key={i} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className={`p-1.5 rounded-lg flex-shrink-0 ${cfg.color}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-800 truncate">{item.name}</div>
                      <div className="text-xs text-gray-400 truncate">
                        {cfg.label}{item.detail ? ` · ${item.detail}` : ''}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 flex-shrink-0">{timeAgo(item.time)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
